// files.ts - typed client for /api/v1/rooms/{roomId}/files endpoints.
// All file content is encrypted client-side before upload.

export interface UploadFileRequest {
  itemId: string;
  nonce: string;       // base64 of 12-byte AES-GCM IV (for metadata)
  aad: string;         // base64
  ciphertext: string;  // base64 of metadata ciphertext
  blobNonce: string;   // base64 of 12-byte IV (for file content)
  blobAAD: string;     // base64
  blob: string;        // base64 of encrypted file content
  filename: string;    // base64 of encrypted filename
  mimeType: string;    // base64 of encrypted MIME type
  size: number;
}

export interface UploadFileResponse {
  itemId: string;
  blobId: string;
  createdAt: string;
  expiresAt: string;
}

export async function uploadFile(
  roomId: string,
  accessToken: string,
  req: UploadFileRequest,
): Promise<UploadFileResponse> {
  const resp = await fetch(`/api/v1/rooms/${roomId}/files`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Room-Access-Token': accessToken,
    },
    body: JSON.stringify(req),
  });
  if (!resp.ok) throw await parseError(resp);
  return resp.json();
}

export async function downloadFile(
  roomId: string,
  blobId: string,
  accessToken: string,
): Promise<ArrayBuffer> {
  const resp = await fetch(`/api/v1/rooms/${roomId}/files/${blobId}`, {
    headers: { 'X-Room-Access-Token': accessToken },
  });
  if (!resp.ok) throw await parseError(resp);
  return resp.arrayBuffer();
}

async function parseError(resp: Response): Promise<Error> {
  try {
    const body = await resp.json();
    const err = body.error;
    if (err && err.code) {
      return new ApiError(err.code, resp.status, err.requestId ?? '', err.message ?? 'unknown error');
    }
  } catch {
    // Non-JSON response.
  }
  return new ApiError('UNKNOWN', resp.status, '', `HTTP ${resp.status}`);
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly requestId: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
